import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import MockResponse from './mock-response.json';

/***********************
 * Coinbase API Service
 * This abstraction supports both real API calls and a "mock" mode.
 ***********************/
const MOCK_ENABLE = false; // Change to 'real' when integrating the real Coinbase API
const API_URL = 'https://x9f3anotde.execute-api.us-east-1.amazonaws.com/Prod'
const REFRESH_INTERVAL = 10 * 60; // Refresh interval in seconds

const CoinbaseAPI = {
  async getPorfolioInfo(apiKey) {
    if (MOCK_ENABLE) {
      // Return sample asset data for testing
      return Promise.resolve(JSON.parse(JSON.stringify(MockResponse)));
      // return Promise.resolve([
      //   { symbol: 'BTC', currentPrice: 32000, balance: 0.5 },
      //   { symbol: 'XRP', currentPrice: 3.29, balance: 100 },
      //   { symbol: 'USD', currentPrice: 1, balance: 500 },
      //   { symbol: 'USDC', currentPrice: 1, balance: 250 },
      // ]);
    }
    // TODO: Replace with real API call using fetch or axios, including error handling
    try {
      const response = await fetch(`${API_URL}/v1/info`, {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized: Invalid API key');
        }
        if (response.status === 403) {
          throw new Error('Forbidden: Access denied');
        }
        if (response.status === 404) {
          throw new Error('Not Found: Resource not found');
        }
        if (response.status === 500) {
          throw new Error('Internal Server Error: Please try again later');
        }
        throw new Error('Fetch error');
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  },
};

/***********************
 * Main Application Component
 ***********************/
function App() {
  const [apiKey, setApiKey] = useState(localStorage.getItem('coinbaseApiKey') || '');
  const [, setAssets] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [, setOrders] = useState([]);
  const [positions, setPositions] = useState({});
  const [error, setError] = useState(null);
  const [isPromptVisible, setIsPromptVisible] = useState(apiKey ? false : true);
  const [loading, setLoading] = useState(false); // New loading state
  const [lastRefresh, setLastRefresh] = useState(null); // New state for last refresh time
  const [useAltView, setUseAltView] = useState(false); // New state for alternative view
  const [isPulling, setIsPulling] = useState(false);
  const [pullProgress, setPullProgress] = useState(0);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const contentRef = useRef(null);
  const touchStartY = useRef(0);
  const lastScrollTop = useRef(0);

  // Compute combined USD value (USD + USDC)
  const portfolioBreakdown = portfolio.breakdown || {};
  const portfolioBalances = portfolioBreakdown.portfolio_balances || {};
  const totalCashEquivalent = portfolioBalances.total_cash_equivalent_balance || {};
  const totalFiatValue = parseFloat(totalCashEquivalent.value || '0.0');
  const cryptoAssetInfo = ((portfolio || {}).breakdown || {}).spot_positions || [];

  // Helper: compute net value for a transaction
  const computeNetValue = (tx, currentPrice) => {
    let currentValue = tx.sellValue;
    if (tx.closed === null || tx.sellValue === undefined) {
      const unrealizedValue = currentPrice * tx.quantity;
      currentValue = unrealizedValue + (tx.sellValue || 0);
    }
    return currentValue - (parseFloat(tx.fill_value) || 0) - tx.totalFees;
  };

  // Helper: compute lifetime (in days) of the position
  const computeLifetime = (tx) => {
    let nowOrClosed = tx.closed ? new Date(tx.closed) : new Date();
    const diffTime = Math.abs(nowOrClosed - new Date(tx.created));
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  const computeAge = (date) => {
    const now = new Date();
    const diffTime = Math.abs(now - new Date(date));
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  }

  const computePositions = (portfolio, assets, orders) => {
    function productMerge(product) {
      if (product.endsWith('USD') && !product.endsWith('USDC')) {
        product = product + 'C';
      }
      return product.split('-')[0];
    }
    function dateCompare(a, b) {
      if (!a) return -1;
      if (!b) return -1;
      return (new Date(a) > new Date(b) ? 1 : -1);
    }
    function priceCompare(a, b) {
      return (parseFloat(a) > parseFloat(b) ? 1 : -1);
    }

    const spot_positions = (portfolio.breakdown || {}).spot_positions || [];
    const assetMap = new Map(spot_positions.map(p => [p.asset, parseFloat(p.total_balance_crypto || 0)]));
    const openOrders = orders.filter(tx => {
      const currency = productMerge(tx.product);
      const hasBalance = assetMap.get(currency) !== undefined;
      const hasNonZeroBalance = hasBalance && assetMap.get(currency) > 0;
      console.log(currency, assetMap.get(currency), hasBalance, hasNonZeroBalance);
      return hasNonZeroBalance;
    })
    const rawPositions = Object.groupBy(openOrders, tx => productMerge(tx.product));
    // console.log(rawPositions)
    let newPositions = {};
    for (const [currency, value] of Object.entries(rawPositions)) {
      // if (currency !== 'XRP') continue;
      let currencyPosition = value.filter(tx => tx.side.toLowerCase() === 'buy' && tx.fill_size !== '0');
      currencyPosition.sort((a, b) => {
        if (a.fill_price === b.fill_price) return dateCompare(a.created, b.created);
        return priceCompare(a.fill_price, b.fill_price);
      });
      currencyPosition.forEach((tx) => {
        tx.quantity = parseFloat(tx.fill_size);
        tx.sellValue = 0;
        tx.closed = null;
        tx.totalFees = parseFloat(tx.fee);
        tx.sellOrders = [];
      })

      // console.log('Currency Position', currencyPosition);
    
      // console.log(currency)
      // console.log(currencyPosition)
      // for (let p of currencyPosition) console.log(p.created);

      // Loop through sell orders and reconcile with buy orders
      let sellOrders = value.filter(tx => tx.side.toLowerCase() === 'sell' && tx.fill_size !== '0');
      sellOrders.sort((a, b) => dateCompare(a.created, b.created));

      for (let i=0; i<sellOrders.length; i++) {
        const sellOrder = sellOrders[i];
        if (sellOrder.remainingQuantity === undefined) {
          sellOrder.remainingQuantity = parseFloat(sellOrder.fill_size);
        }

        // console.log(i, "Sell Order: ", sellOrder.remainingQuantity, sellOrder);

        let remainingQuantity = sellOrder.remainingQuantity;
        let buyOrderIndex = 0;
        while (remainingQuantity > 0 && buyOrderIndex < currencyPosition.length) {
          const buyOrder = currencyPosition[buyOrderIndex];

          if (buyOrder.closed) {
            // console.log(`-> [${buyOrderIndex}] Buy Order (already closed): `, buyOrder.closed, buyOrder.created);
            buyOrderIndex++;
            continue;
          }

          // console.log(`-> [${buyOrderIndex}] Buy Order: `, buyOrder.created, buyOrder.fill_value, remainingQuantity, buyOrder);

          if (dateCompare(sellOrder.created, buyOrder.created) < 0) {
            // console.log(`-> [${buyOrderIndex}] Buy Order (skipped): `, buyOrder.created, sellOrder.created, );
            buyOrderIndex++;
            continue;
          }

          const sellPrice = parseFloat(sellOrder.fill_price);
          if (buyOrder.quantity > remainingQuantity) {
            // console.log(`-> [${buyOrderIndex}] Buy Order (still open): `, buyOrder.fill_size, buyOrder.quantity, remainingQuantity);
            buyOrder.quantity -= remainingQuantity;
            buyOrder.sellValue += remainingQuantity * sellPrice;
            buyOrder.totalFees += parseFloat(sellOrder.fee);
            buyOrder.sellOrders.push({
              created: sellOrder.created,
              price: sellOrder.fill_price,
              size: remainingQuantity,
              fee: sellOrder.fee,
            });
            remainingQuantity = 0;
          } else {
            // console.log(`-> [${buyOrderIndex}] Buy Order (closed): `, buyOrder.fill_size, buyOrder.quantity, remainingQuantity);
            buyOrder.sellValue += buyOrder.quantity * sellPrice;
            buyOrder.sellOrders.push({
              created: sellOrder.created,
              price: sellOrder.fill_price,
              size: buyOrder.quantity,
              fee: sellOrder.fee,
            });
            remainingQuantity -= buyOrder.quantity;
            buyOrder.totalFees += parseFloat(sellOrder.fee) * (buyOrder.quantity / parseFloat(sellOrder.fill_size));
            buyOrder.quantity = 0;
            buyOrder.closed = sellOrder.created;
            buyOrderIndex++;
          }
        }
      }
      newPositions[currency] = currencyPosition.sort((a, b) => {
        if (a.closed === b.closed) {
          if (!a.closed && !b.closed) {
            return parseFloat(a.fill_price) - parseFloat(b.fill_price) > 0 ? 1 : -1;
          }
          return dateCompare(b.created, a.created);
        }
        return dateCompare(b.closed, a.closed);
      });
    }

    console.log('Positions', newPositions)
    setPositions(newPositions);
  }


  // Refresh data from Coinbase API
  const refreshData = useCallback(async () => {
    if (!apiKey) return;
    setLoading(true);
    setShowLoadingOverlay(true);
    try {
      const [profile] = await Promise.all([
        CoinbaseAPI.getPorfolioInfo(apiKey),
        new Promise((resolve) => setTimeout(() => resolve(true), 1000)), // Mock transactions
      ]);

      // Parse assets & transactions
      const account = profile.account || {};
      const porfolio = profile.portfolio || {};
      const orders = profile.orders || {};
      const assets = account.accounts || [];
      setAssets(assets);
      setPortfolio(porfolio || {});

      const sortedOrders = orders.map((tx) => tx);
      sortedOrders.sort((a, b) => a.created > b.created);
      setOrders(sortedOrders);

      computePositions(porfolio, assets, sortedOrders);

      setError(null);
      setLastRefresh(new Date()); // Update last refresh time
    } catch (err) {
      console.error(err);

      setError(err.message);
      
      if (err.message === 'Unauthorized: Invalid API key') {
        localStorage.removeItem('coinbaseApiKey');
        setApiKey('');
      }

      setIsPromptVisible(true);

    } finally {
      setLoading(false);
      // Add a small delay before hiding the loading overlay for smoother transition
      setTimeout(() => setShowLoadingOverlay(false), 200);
    }
  }, [apiKey]);

  // Save API key and fetch data on key submission
  const handleApiKeySubmit = (key) => {
    setApiKey(key);
    localStorage.setItem('coinbaseApiKey', key);
    setIsPromptVisible(false);
  };

  // Refresh on mount
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Format the last refresh time
  const formatLastRefresh = (date) => {
    if (!date) return '';
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    return isToday ? date.toLocaleTimeString() : `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  const needsRefresh = () => {
    if (!lastRefresh) return true;
    const now = new Date();
    const diffTime = Math.abs(now - lastRefresh);
    const diffMinutes = Math.floor(diffTime / 1000);
    return diffMinutes > REFRESH_INTERVAL; // Refresh if more than REFRESH_INTERVAL seconds have passed
  }

  const handleTouchStart = useCallback((e) => {
    if (contentRef.current.scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
      lastScrollTop.current = 0;
    }
  }, []);

  const handleTouchMove = useCallback(async (e) => {
    if (!touchStartY.current) return;
    
    const touchY = e.touches[0].clientY;
    const diff = touchY - touchStartY.current;
    
    // Ensure we're at the top and scrolling down
    if (contentRef.current.scrollTop <= 0) {
      if (diff > 0) {
        setIsPulling(true);
        const progress = Math.min(diff / 60, 1);
        setPullProgress(progress);
        
        // Prevent default only when we're actually pulling to refresh
        if (isPulling) {
          e.preventDefault();
          e.stopPropagation();
        }

        // Trigger refresh when progress hits 100%
        if (progress >= 1 && !loading) {
          await refreshData();
          touchStartY.current = 0;
          setIsPulling(false);
          setPullProgress(0);
        }
      }
    } else {
      // Reset if we're not at the top
      touchStartY.current = 0;
      setIsPulling(false);
      setPullProgress(0);
    }
  }, [isPulling, loading, refreshData]);

  const handleTouchEnd = useCallback(() => {
    touchStartY.current = 0;
    setIsPulling(false);
    setPullProgress(0);
  }, []);

  // Add passive: false to allow preventDefault()
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const options = { passive: false };
    
    content.addEventListener('touchstart', handleTouchStart, options);
    content.addEventListener('touchmove', handleTouchMove, options);
    content.addEventListener('touchend', handleTouchEnd);

    return () => {
      content.removeEventListener('touchstart', handleTouchStart, options);
      content.removeEventListener('touchmove', handleTouchMove, options);
      content.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]); // Include all handler functions

  const handleLogout = () => {
    // localStorage.removeItem('coinbaseApiKey');
    // setApiKey('');
    setIsPromptVisible(true);
  };

  //
  // Positions
  //

  // Render API key prompt if needed
  if (isPromptVisible) {
    return (
      <div className="api-key-prompt">
        <h2>Enter your Coinbase API Credentials</h2>
        {error && <p className="error">{error}</p>}
        <ApiKeyForm onSubmit={handleApiKeySubmit} />
      </div>
    );
  }

  const assetPriceMap = new Map(cryptoAssetInfo.map(asset => [
    asset.asset,
    parseFloat(asset.total_balance_fiat) / parseFloat(asset.total_balance_crypto)
  ]));

  function computeTotalValue() {
    let totalValue = 0;
    for (const assetSymbol of Object.keys(positions)) {
      const assetPositions = positions[assetSymbol];
      const currentPrice = assetPriceMap.get(assetSymbol);
      for (const tx of assetPositions) {
        if (tx.closed) {
          totalValue += computeNetValue(tx, currentPrice);
        }
      }
    }
    return totalValue;
  }
  const totalValue = computeTotalValue();

  const assetAllocationMap = new Map(cryptoAssetInfo.map(asset => [asset.asset, asset.allocation || 0]));
  const orderedAssetSymbols = Object.keys(positions).sort((a, b) => {
    return (assetAllocationMap.get(b) || 0) - (assetAllocationMap.get(a) || 0);
  });
    
  return (
    <div className="app">
      <nav className="navbar">
        <h1>Crypto Positions</h1>
        <div className="refresh-info">
          {lastRefresh && <span className="last-refresh">Updated {formatLastRefresh(lastRefresh)}</span>}
          <button onClick={refreshData} className="refresh-btn" disabled={loading} style={{ backgroundColor: needsRefresh() ? '#e62240' : '' }}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </nav>
      <div 
        className="content" 
        ref={contentRef}
      >
        <div 
          className={`loading-overlay ${(loading || showLoadingOverlay) ? 'refreshing' : ''}`}
          style={{ transform: `translateY(${isPulling ? -100 + (pullProgress * 100) : (loading || showLoadingOverlay) ? '0' : '-100'}%)` }}
        >
          <div className="spinner"></div>
        </div>
        <div 
          className={`content-inner ${(loading || showLoadingOverlay) ? 'refreshing' : ''}`} 
          style={{ transform: `translateY(${isPulling ? pullProgress * 60 : (loading || showLoadingOverlay) ? '60' : '0'}px)` }}
        >
          {/* Section 1: Fiat Balance */}
          <section className="fiat-balance">
            <h2>Total Fiat Balance (USD + USDC)</h2>
            <p className="balance-amount">${totalFiatValue.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              <span style={{ color: !totalValue ? 'inherit' : totalValue > 0 ? 'green' : 'red', fontSize: '10pt'}}>{totalValue > 0 ? '+' : ''}${totalValue.toFixed(2)}</span>
            </p>
          </section>
          {/* Section 2: Positions by Asset */}
          <section className="transactions">
            {orderedAssetSymbols.map((assetSymbol) => {
              const currentPrice = assetPriceMap.get(assetSymbol);
              const assetInfo = cryptoAssetInfo.find((a) => a.asset === assetSymbol);
              const assetTotalValue = positions[assetSymbol].reduce((total, tx) => total + computeNetValue(tx, currentPrice), 0)
              return (
                <div key={assetSymbol} className="asset-group">
                  <div className="asset-header">
                    <h3>
                      {assetSymbol} ({(Math.floor(assetInfo?.allocation * 1000)/10)?.toFixed(1)}%) <span style={{ 
                        color: assetTotalValue === 0 ? 'inherit' : assetTotalValue > 0 ? 'green' : 'red'
                      }}>
                        ${assetTotalValue.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    </h3>
                    <p>{currentPrice ? `$${currentPrice.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : 'N/A'}</p>
                  </div>
                  <div className="transactions-scroll">
                    {positions[assetSymbol].map((tx, index) => {
                      const netValue = computeNetValue(tx, currentPrice);
                      const lifetime = computeLifetime(tx);
                      const price = parseFloat(tx.fill_price).toFixed(2);
                      const fillValue = parseFloat(tx.fill_value).toFixed(2);
                      const age = computeAge(tx.closed || tx.created);

                      const priceData = useAltView ? `${(tx.fill_size - tx.quantity).toFixed(8).replace(/\.?0+$/, "")} of ${tx.fill_size} ${assetSymbol}` : (`$${fillValue} @ $${price}`);

                      const netValueNum = useAltView ? (netValue / fillValue*100).toFixed(1) : netValue.toFixed(2);
                      const netValueData =  useAltView ? `${netValueNum > 0 ? '+' : ''}${netValueNum}%` : `$${netValueNum}`;

                      return (
                        <div
                          key={index}
                          className="transaction-card"
                          onClick={() => {setUseAltView(!useAltView)}}
                          style={{
                            backgroundColor: tx.closed
                              ? '#DDDDDD'
                              : netValue >= 0
                              ? '#d4edda'
                              : '#edd4da',
                          }}
                        >
                          <p className="price">{priceData}</p>
                          <p className="net-value" style={{ color: netValueNum >= 0 ? 'green' : 'red' }}>
                            {netValueData}
                          </p>
                          <p className="lifetime">{tx.closed ? `${age}d ago` : `(${lifetime}d)`}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </section>
          <button className="logout-button" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}

/***********************
 * API Key Form Component
 ***********************/
function ApiKeyForm({ onSubmit }) {
  const [key, setKey] = useState(localStorage.getItem('coinbaseApiKey') || '');
  const handleSubmit = (e) => {
    e.preventDefault();
    if (key.trim() !== '') {
      onSubmit(key.trim());
    }
  };
  return (
    <form onSubmit={handleSubmit} className="api-key-form">
      <input
        type="text"
        placeholder="eyJasdga..."
        value={key}
        onChange={(e) => setKey(e.target.value)}
      />
      <button type="submit">Let's Go!</button>
    </form>
  );
}

export default App;
